<script setup lang="ts">

import MainContainer from '@/components/MainContainer.vue'
import {
  computed,
  type ComputedRef,
  inject,
  markRaw,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import DailyComboSection from '@/components/DailyComboSection.vue'
import { syncPlayer, usePlayer } from '@/composables/game/player'
import { useConfig, useUpgradesConfig } from '@/composables/game/config'
import PlayerUpgradeCard from '@/components/PlayerUpgradeCard.vue'
import { ECOMMERCE_ITEM_PRICE_TYPE, type PlayerUpgrade } from '@/types/shared/player-config'
import BuyUpgradeModal from '@/components/modals/BuyUpgradeModal.vue'
import TapSection from '@/components/TapSection.vue'
import { useAnimateCoins, useFlyingCoins } from '@/composables/game/flying-coins'
import CoinsTotalWrapper from '@/components/CoinsTotalWrapper.vue'
import { T, useTranslate } from '@tolgee/vue'
import BadgeOverlay from '@/components/overlays/BadgeOverlay.vue'
import type { Badge } from '@/types/shared/player'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { GameSound, useSounds } from '@/composables/game/sounds'
import { useSpecialCards } from '@/composables/game/special-cards'
import { useNow } from '@/composables/game/time'
import { UserGuide } from '@/types/onboarding'
import { useGuide } from '@/composables/game/guide'
import UserGuideModal from '@/components/modals/UserGuideModal.vue'
import LeafImage from '@/assets/images/navbar-icons/nav-improve-icon.webp'
import {
  useBatchFastForward,
  useCardBattle,
  useNewUpgradesChecker, useUpgradableCardsCount,
  useUpgradeCooldown,
} from '@/composables/game/upgrade'
import DailyComboLastItemModal from '@/components/modals/DailyComboLastItemModal.vue'
import DailyComboOverlay from '@/components/overlays/DailyComboOverlay.vue'
import DailyComboCompleteOverlay from '@/components/overlays/DailyComboCompleteOverlay.vue'
import SpinnerIcon from '@/components/icons/SpinnerIcon.vue'
import { useToaster } from '@/composables/game/toasters'
import { useDailyCombo } from '@/composables/game/daily'
import { useSkin } from '@/composables/game/skin'
import BannerCarousel from '@/components/BannerCarousel.vue'

import BatchSpeedUpBanner from '@/components/BatchSpeedUpBanner.vue'
import BatchSpeedUpModal from '@/components/BatchSpeedUpModal.vue'
import SkinImage from '@/assets/images/skins/mascot-s3.webp'
import MineExplodedModal from '@/components/modals/MineExplodedModal.vue'
import MineInfoModal from '@/components/modals/MineInfoModal.vue'
import AntiMineBlockedModal from '@/components/modals/AntiMineBlockedModal.vue'
import { useMultiplayerMine } from '@/composables/game/multiplayer-mine'
import type { UpgradeEvent } from '@/types/shared/player'
import mineIcon from '@/assets/images/multiplayer/mine-icon.png'
import mineInfoIcon from '@/assets/images/multiplayer/mine-info-icon.svg'
import bombsExplodingImg from '@/assets/images/multiplayer/bombs-exploding.webp'
import SearchIcon from '@/components/icons/SearchIcon.vue'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import BatchUpgradeCardModal from '@/components/BatchUpgradeCardModal.vue'

const WebApp = Telegram.WebApp
const isTapping = ref(false)
const { player, upgrade, isUpgrading, hasUpgraded, newBadges, minesCount } = usePlayer()
const { config } = useConfig()
const { upgrades } = useUpgradesConfig()
const { addHapticFeedback, addNotificationFeedback } = useHapticFeedback()
const { playSound } = useSounds()
const { getSpecialBgColor } = useSpecialCards()
const { nowDateMinuteTimestamp } = useNow()
const { shouldShowGuide, removeGuide } = useGuide()
const { t } = useTranslate()
const { formattedTime, remainedTime } = useUpgradeCooldown(computed(() => selectedUpgrade.value))
const { startCoinAnimation } = useFlyingCoins()
const { notify } = useToaster()
const { doAnimateCoins } = useAnimateCoins()
const {
  foundCombo,
  foundComboCards,
  playerStatsAfterDailyCombo,
  resetPlayerStatsForDailyCombo,
} = useDailyCombo()
const { currentSkin } = useSkin()
const { isInBattleConflict } = useCardBattle()
const { showMineInfoModal, openMineInfoModal, closeMineInfoModal } = useMultiplayerMine()
const { isUpgradeNew } = useNewUpgradesChecker()
const {
  batchFastForwardPurchased,
  canBatchFastForward,
  batchDiscount,
  ecommerceItem: batchFastForwardEcommerceItem,
} = useBatchFastForward()
const { affordableCount, totalProfitDelta, totalCoinsToSpend } = useUpgradableCardsCount()

const coinsToShow = inject<ComputedRef<number>>('coinsToShow') as ComputedRef<number>
const showGuideModal = ref(false)
const showBatchUpgradeCardModal = ref(false)

const selectedSection = ref(config.value.upgradeSections[0])
const isSpecialsSectionSelected = computed(() => selectedSection.value.toLocaleLowerCase() === 'specials')

const searchQuery = ref('')
const searchInput = ref<HTMLInputElement | null>(null)
const isSearchFocused = ref(false)
const isSearchActive = computed(() => searchQuery.value.trim().length > 0)

const selectedSectionUpgrades = computed(() => {
  let upgradesArr = isSearchActive.value
    ? upgrades.value.filter(u => u.name.toLowerCase().includes(searchQuery.value.trim().toLowerCase()))
    : upgrades.value.filter(u => u.section === selectedSection.value)

  // Highlight sorting first
  upgradesArr = upgradesArr.sort((u1, u2) => {
    if (u1.highlight) return -1
    if (u2.highlight) return 1
    return 0
  })

  if (!isSearchActive.value && isSpecialsSectionSelected.value) {
    const now = nowDateMinuteTimestamp.value
    const notExpired = upgradesArr.filter(u => (u.expiresOn ?? Infinity) > now)
    const expired = upgradesArr.filter(u => (u.expiresOn ?? Infinity) <= now)
    notExpired.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
    expired.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
    upgradesArr = [...notExpired, ...expired]
  } else {
    // Keep previous expiresOn sort for non-specials
    upgradesArr = upgradesArr.sort((u1, u2) => {
      const u1Expiry = u1.expiresOn ?? Infinity
      const u2Expiry = u2.expiresOn ?? Infinity
      if (u1Expiry > nowDateMinuteTimestamp.value && u2Expiry > nowDateMinuteTimestamp.value) return 0
      if (u1Expiry < nowDateMinuteTimestamp.value && u2Expiry < nowDateMinuteTimestamp.value) return 0
      return u1Expiry > nowDateMinuteTimestamp.value ? -1 : 1
    })
  }
  return upgradesArr
})

const hasNoSearchResults = computed(() => isSearchActive.value && selectedSectionUpgrades.value.length === 0)

function clearSearch() {
  searchQuery.value = ''
  searchInput.value?.blur()
}

function onSearchFocus() {
  isSearchFocused.value = true
  nextTick(() => {
    searchInput.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  })
}

const selectedUpgrade = ref<PlayerUpgrade | null>(null)
const showBuyUpgradeModal = computed(() => selectedUpgrade.value !== null)
const foundBadge = ref<Badge | null>(null)
const showBadgeOverlay = computed(() => foundBadge.value !== null)
const canUpgradeForStars = computed(() => player.value.level >= 5)
const scrollPosition = ref(0)

const showDailyComboOverlay = ref(false)
const showLastComboModal = ref(false)
const showDailyComboCompleteOverlay = ref(false)
const showMineExplodedModal = ref(false)
const showMineRedPulse = ref(false)
const showMineBombsFlying = ref(false)
const upgradeEventData = ref<UpgradeEvent | null>(null)
const showAntiMineBlockedModal = ref(false)
const minedCardId = ref<string | null>(null)

const foundComboLength = computed(() => foundCombo.value.length)

const cooldownForAnimation = ref(0)
const cooldownAnimationInitialStartDashoffset = ref(0)
const elapsedTimeForAnimation = ref(0)

const hasCooldown = computed(() => !!selectedUpgrade.value && selectedUpgrade.value.cooldown > 0 && formattedTime.value !== undefined)

const showBanner = computed(() => WebApp.initDataUnsafe.user?.username !== 'Fom98')

const batchSpeedUpModalProgramaticallyPoppedUp = ref(false)
const showBatchSpeedUpModal = ref(false)

async function doUpgrade(purchase: boolean, priceType?: ECOMMERCE_ITEM_PRICE_TYPE) {
  if (!selectedUpgrade.value) return
  const upgradedCardId = selectedUpgrade.value.id
  const result = await upgrade(selectedUpgrade.value.id, selectedUpgrade.value.price, purchase ? selectedUpgrade.value.ecommerce.fullUpgrade.prices.find(price => price.type === (priceType ?? ECOMMERCE_ITEM_PRICE_TYPE.STAR)) : null)
  console.log('result', result)
  if (!result?.event) return
  if (result?.event.name === 'mine') {
    selectedUpgrade.value = null
    upgradeEventData.value = result.event
    minedCardId.value = upgradedCardId
    await syncPlayer(true)
    showMineRedPulse.value = true
    setTimeout(() => {
      showMineRedPulse.value = false
      showMineBombsFlying.value = true
      setTimeout(() => {
        showMineBombsFlying.value = false
        showMineExplodedModal.value = true
      }, 700)
    }, 2000)
  } else if (result?.event.name === 'antiMine') {
    selectedUpgrade.value = null
    upgradeEventData.value = result.event
    await syncPlayer(true)
    setTimeout(() => {
      showAntiMineBlockedModal.value = true
    }, 400)
  }
}

function getCardBgColor(index: number) {
  if (isSpecialsSectionSelected.value) {
    return getSpecialBgColor(index)
  }
  return undefined
}

function onPageScroll(event: Event) {
  scrollPosition.value = (event.target as Element).scrollTop
}

function resetCooldown() {
  if (selectedUpgrade.value) {
    cooldownForAnimation.value = remainedTime.value
    elapsedTimeForAnimation.value = selectedUpgrade.value.cooldown - (cooldownForAnimation.value)
    cooldownAnimationInitialStartDashoffset.value = -(elapsedTimeForAnimation.value / selectedUpgrade.value.cooldown) * 100
  }
}

watch(foundComboLength, (foundComboLength) => {
  if (foundComboLength === 1 || foundComboLength === 2) {
    setTimeout(() => showDailyComboOverlay.value = true, 800)
  }
})

watch(playerStatsAfterDailyCombo, (playerStatsForDailyCombo, oldValue) => {
  if (!oldValue && !!playerStatsForDailyCombo) {
    setTimeout(() => showLastComboModal.value = true, 300)
  }
})

watch(showLastComboModal, showLastComboModal => {
  if (showLastComboModal) return
  notify({
    title: t.value('daily_combo.requesting_claim', 'Requesting claim...'),
    icon: markRaw(SpinnerIcon),
    duration: 1000,
  })
  doAnimateCoins()
  setTimeout(() => {
    notify({ title: t.value('daily_combo.claim_success', 'Claim success!'), type: 'success' })
    startCoinAnimation()
    resetPlayerStatsForDailyCombo()
    playSound(GameSound.PATHWAY_COMPLETED)
    // Show combo complete overlay after claim success
    setTimeout(() => showDailyComboCompleteOverlay.value = true, 500)
  }, 1000)
})

function onHideBatchSpeedUpModal() {
  showBatchSpeedUpModal.value = false
  setTimeout(() => batchFastForwardPurchased.value = false, 300)
}

onBeforeMount(() => {
  showGuideModal.value = shouldShowGuide(UserGuide.Improve)
})

onMounted(() => {
  const scrollTarget = document.querySelector('.scroll-container')
  if (scrollTarget) {
    scrollTarget.addEventListener('scroll', onPageScroll)
  }
})

onBeforeUnmount(() => {
  const scrollTarget = document.querySelector('.scroll-container')
  if (scrollTarget) {
    scrollTarget.removeEventListener('scroll', onPageScroll)
  }
})

watch(hasUpgraded, () => {
  addHapticFeedback('medium')
  addNotificationFeedback('success')
  playSound(GameSound.UPGRADE_SUCCESS)
  startCoinAnimation(50)
  if (!selectedUpgrade.value) return

  selectedUpgrade.value = null
})

watch(newBadges, newBadges => {
  if (!newBadges || !newBadges.length) return
  setTimeout(() => foundBadge.value = newBadges[0], 150)
})

watch(selectedSection, () => {
  const scrollTarget = document.querySelector('.scroll-container')
  if (scrollTarget && scrollTarget.scrollTop > 274) {
    scrollTarget.scrollTop = 274
  }
})

watch(selectedUpgrade, (newVal) => {
  if (newVal) {
    resetCooldown()
    return
  }
})

watch(canBatchFastForward, canBatchFastForward => {
  if (!canBatchFastForward || batchSpeedUpModalProgramaticallyPoppedUp.value) return
  showBatchSpeedUpModal.value = true
  batchSpeedUpModalProgramaticallyPoppedUp.value = true
})

onMounted(() => {
  if (hasCooldown.value) {
    resetCooldown()
  }
})
</script>

<template>
  <MainContainer
    class="!pt-[18px] pb-6"
    :class="{'touch-none': isTapping}"
  >
    <div class="w-full h-full flex-1 flex flex-col">
      <BannerCarousel
        v-if="showBanner"
        class="mb-9"
      />
      <div
        v-if="minesCount > 0"
        class="mb-3 px-3 py-1 bg-dark-top-layer/[.66] backdrop-blur-[12px] border-2 border-dark-top-most-layer rounded-3xl"
        :class="{ 'mine-banner': minesCount > 0 }"
      >
        <div class="flex items-center gap-2">
          <img
            :src="mineIcon"
            class="w-8 h-8 shrink-0 object-contain"
            alt=""
          >
          <span class="flex-1 text-base text-white leading-5 font-semibold">
            {{ minesCount }} active mine{{ minesCount !== 1 ? 's' : '' }} on your cards
          </span>
          <img
            :src="mineInfoIcon"
            class="w-6 h-6 shrink-0"
            :class="{'cursor-pointer': minesCount > 0}"
            alt="Info"
            @click="openMineInfoModal"
          >
        </div>
      </div>
      <DailyComboSection :class="scrollPosition < 40 ? 'z-[81]' : 'z-[1]'" />
      <CoinsTotalWrapper
        class="z-[1] my-6 justify-center"
        :coins="coinsToShow"
      />
      <div
        v-if="affordableCount > 1"
        class="-mt-5 mb-3 mx-auto text-white text-shadow-[0_0_56px_rgba(0,0,0,0.5)] text-sm leading-[14px] font-bold uppercase relative rounded-md bg-white/[.12] h-7 min-h-[28px] overflow-hidden flex items-center px-2"
        @click="showBatchUpgradeCardModal = true"
      >
        <span>batch upgrade all cards</span>
        <i
          class="shiny"
        />
      </div>
      <BatchSpeedUpBanner
        v-if="canBatchFastForward"
        :discount="batchDiscount"
        @click="showBatchSpeedUpModal = true"
      />
      <div class="z-[81] sticky top-[66px] mb-4 flex flex-col gap-3">
        <div
          class="w-full flex flex-wrap items-center bg-dark-top-layer/[.66] rounded-xl p-0.5 bounce-in-left backdrop-blur-xl"
        >
          <div
            v-for="section in config.upgradeSections"
            :key="section"
            class="flex-1 text-xs leading-4 font-bold py-2 rounded-[10px] text-center"
            :class="section === selectedSection ? 'bg-dark-top-most-layer text-light-white' : 'text-white/[.38]'"
            @click="addHapticFeedback(); selectedSection = section"
          >
            <T
              :key-name="`cards.section.${section.toLowerCase().replace(/ /g, '_')}`"
              :default-value="section"
            />
          </div>
        </div>
        <div
          class="flex items-center gap-2 px-2 py-1.5 rounded-xl border transition-colors border-dark-top-layer/[.88] backdrop-blur-xl"
          :class="isSearchFocused ? 'bg-dark-top-layer/[.88]' : 'bg-dark-top-layer/[.66]'"
        >
          <SearchIcon class="w-6 h-6 text-white shrink-0" />
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            placeholder="Search cards"
            class="flex-1 bg-transparent text-sm text-white placeholder-white/[.44] font-medium outline-none"
            @focus="onSearchFocus"
            @blur="isSearchFocused = false"
          >
          <button
            v-if="isSearchActive"
            class="shrink-0 text-white/[.87]"
            @click="clearSearch"
          >
            <CrossCircleIcon class="w-5 h-5" />
          </button>
        </div>
      </div>
      <div
        v-if="hasNoSearchResults"
        class="text-center w-full mx-auto my-8 px-2 max-w-[329px]"
      >
        <span class="text-xl font-bold text-light-white">
          Nothing was found, please search for something else!
        </span>
      </div>
      <div
        v-else
        class="mt-3 mb-8 grid gap-3 grid-cols-2 z-[2]"
      >
        <PlayerUpgradeCard
          v-for="(upgradeObj, index) in selectedSectionUpgrades"
          :key="upgradeObj.id"
          class="flex-1"
          :upgrade-list="upgrades"
          :upgrade="upgradeObj"
          :player="player"
          :is-special="isSpecialsSectionSelected"
          :is-highlight="!!upgradeObj.highlight"
          :bg-color="getCardBgColor(index)"
          :can-upgrade-for-stars="canUpgradeForStars"
          :index="index"
          :is-in-battle-conflict="isInBattleConflict(upgradeObj)"
          :is-new="isUpgradeNew(upgradeObj.id)"
          :is-mined="upgradeObj.id === minedCardId"
          @card-click="selectedUpgrade = upgradeObj"
        />
      </div>
      <TapSection
        :back-image="SkinImage"
        hide-inner-image
        tap-container-class="z-[1]"
        fade-image-class="hidden"
        image-wrapper-class="rounded-none"
        image-inner-wrapper-class="rounded-none"
        custom-text="ho"
        @is-tapping="isTapping = $event"
      />
    </div>

    <BuyUpgradeModal
      v-if="showBuyUpgradeModal && selectedUpgrade"
      :show="showBuyUpgradeModal"
      :upgrade="selectedUpgrade"
      :is-upgrading="isUpgrading"
      :elapsed-time="elapsedTimeForAnimation"
      :animation-cooldown="cooldownForAnimation"
      :animation-offset="cooldownAnimationInitialStartDashoffset"
      :has-cooldown="hasCooldown"
      :can-upgrade-for-stars="canUpgradeForStars"
      @hide="selectedUpgrade = null"
      @upgrade="doUpgrade"
      @on-new-upgrade="(value: PlayerUpgrade | null) => selectedUpgrade = value"
    />

    <BadgeOverlay
      v-if="showBadgeOverlay"
      :class="showBadgeOverlay ? 'z-[999] opacity-100' : '-z-[1] opacity-0'"
      :name="foundBadge?.name"
      :image="foundBadge?.imageUrl"
      :show="showBadgeOverlay"
      @click="foundBadge = null"
    />

    <UserGuideModal
      v-if="showGuideModal"
      :show="showGuideModal"
      :image="LeafImage"
      :title="t('guide.improve_title', 'Boost your progress with cards')"
      :description="t('guide.improve_description', `Buy cards to boost your profit per hour and grow your passive income—even while offline! <br/><br/> Check-in daily for card combos that unlock extra bonuses!`)"
      @hide="showGuideModal = false; removeGuide(UserGuide.Improve)"
    />
    <DailyComboOverlay
      v-if="showDailyComboOverlay"
      :combo-cards="foundComboCards"
      :show="showDailyComboOverlay"
      @click="showDailyComboOverlay = false; resetPlayerStatsForDailyCombo()"
    />

    <DailyComboLastItemModal
      v-if="showLastComboModal"
      :show="showLastComboModal"
      @hide="showLastComboModal = false"
    />

    <DailyComboCompleteOverlay
      v-if="showDailyComboCompleteOverlay"
      :show="showDailyComboCompleteOverlay"
      @close="showDailyComboCompleteOverlay = false"
    />

    <BatchSpeedUpModal
      v-if="showBatchSpeedUpModal && (canBatchFastForward || batchFastForwardPurchased) && !!batchFastForwardEcommerceItem"
      :show="showBatchSpeedUpModal && (canBatchFastForward || batchFastForwardPurchased)"
      :discount="batchDiscount"
      :ecommerce-item="batchFastForwardEcommerceItem"
      @hide="onHideBatchSpeedUpModal"
    />

    <div
      v-if="showMineRedPulse"
      class="fixed inset-0 z-[999] pointer-events-none mine-red-pulse"
    />

    <div
      v-if="showMineBombsFlying"
      class="fixed inset-0 z-[1000] pointer-events-none flex items-center justify-center overflow-hidden"
    >
      <img
        :src="bombsExplodingImg"
        alt=""
        class="mine-bomb-zoom"
      >
    </div>

    <MineExplodedModal
      v-if="showMineExplodedModal"
      :show="showMineExplodedModal"
      :coins-lost="upgradeEventData?.goldenCoins"
      :attacker-name="upgradeEventData?.sourceUser.firstName"
      :attacker-avatar-url="upgradeEventData?.sourceUser.photoUrl"
      @hide="showMineExplodedModal = false; upgradeEventData = null; minedCardId = null"
    />

    <AntiMineBlockedModal
      v-if="showAntiMineBlockedModal"
      :show="showAntiMineBlockedModal"
      :golden-coins="upgradeEventData?.goldenCoins ?? 0"
      :source-user-name="upgradeEventData?.sourceUser.firstName ?? ''"
      :source-user-photo-url="upgradeEventData?.sourceUser.photoUrl"
      @hide="showAntiMineBlockedModal = false; upgradeEventData = null"
    />

    <BatchUpgradeCardModal
      v-if="showBatchUpgradeCardModal"
      :show="showBatchUpgradeCardModal"
      :cards-to-upgrade-count="affordableCount"
      :total-profit-delta="totalProfitDelta"
      :total-coins="totalCoinsToSpend"
      @hide="showBatchUpgradeCardModal = false"
    />

    <MineInfoModal
      :show="showMineInfoModal"
      @hide="closeMineInfoModal"
    />
  </MainContainer>
</template>

<style scoped lang="scss">
.mine-red-pulse {
  border: 22px solid #F00;
  filter: blur(20.6px);
  animation: border-pulse 920ms ease-out infinite;
}

@keyframes border-pulse {
  0%, 100% { border-width: 22px; filter: blur(20.6px); }
  50% { border-width: 55px; filter: blur(32.45px); }
}

.mine-banner {
  box-shadow: 0 0 24px 0 red;
  animation: mine-pulse 920ms ease-out infinite;
}

@keyframes mine-pulse {
  0%, 100% { box-shadow: 0 0 24px 0 red; }
  50% { box-shadow: 0 0 48px 8px red; }
}

.mine-bomb-zoom {
  width: 60vw;
  animation: bomb-zoom 700ms ease-in forwards;
  transform-origin: center center;
}

@keyframes bomb-zoom {
  0%   { transform: scale(0.2); opacity: 1; }
  100% { transform: scale(6);   opacity: 0.6; }
}
</style>
